import { View, Text, YStack, XStack } from 'tamagui'
import { ReactNode, useState } from 'react'
import { Badge } from '@components/Badge'
import RenderHtml from 'react-native-render-html'
import { Dimensions, LayoutChangeEvent } from 'react-native'
import { HeroIcon } from '@components/HeroIcon'
import { useThemeColor } from '@hooks/useThemeColor'

interface IQuestionCardProps {
  questionNumber: number
  title: string
  value: number
  children: ReactNode
  statement?: string
}

export function QuestionCard({
  questionNumber,
  title,
  value,
  children,
  statement,
}: IQuestionCardProps) {
  // O HTML da questão precisa acompanhar a largura do container pai (a coluna da
  // aula), não a da janela: no desktop a coluna é bem menor que a janela, e
  // usar Dimensions fazia o conteúdo (texto e imagens) estourar o card.
  const [contentWidth, setContentWidth] = useState<number | null>(null)

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout
    if (width > 0) setContentWidth(width)
  }

  const htmlContentWidth = contentWidth ?? Dimensions.get('window').width - 40

  const textColor = useThemeColor('text-primary')

  return (
    <YStack width="100%" minWidth={0} flexShrink={1}>
      <View
        flexDirection="column"
        alignItems="flex-start"
        gap="$spacing-inset-sm"
        mb="$spacing-inset-sm"
      >
        <XStack
          gap="$spacing-inset-nano"
          alignItems="flex-start"
          width="100%"
          minWidth={0}
        >
          <Text
            fontFamily="$mulishRegular"
            fontSize="$font-size-sm"
            color="$text-primary"
            lineHeight={25}
            fontWeight={700}
            flexShrink={0}
          >
            {questionNumber}.
          </Text>
          {/* flex + minWidth=0: RenderHtml renderiza Views com flexShrink 0, que
              sem esse wrapper crescem até o max-content do enunciado. */}
          <View flex={1} minWidth={0} onLayout={handleLayout}>
            <RenderHtml
              contentWidth={htmlContentWidth}
              source={{ html: title }}
              tagsStyles={{
                p: {
                  marginTop: 0,
                },
              }}
              baseStyle={{
                color: textColor,
                fontSize: 20,
                fontFamily: 'Mulish-Regular',
                lineHeight: 25,
              }}
            />
          </View>
        </XStack>

        <View flexDirection="row" alignItems="center" gap="$spacing-inset-nano">
          <Text
            fontSize="$font-size-xs"
            color="$text-primary"
            fontFamily="$mulishRegular"
          >
            Vale:
          </Text>
          <Badge text={`${value} pontos`} variant="info" />
        </View>
      </View>

      {children}

      {statement && (
        <View
          backgroundColor="$bg-surface-raised"
          borderRadius="$border-radius-sm"
          p="$spacing-inset-xs"
          mb="$spacing-inset-xs"
          mt="$spacing-inset-sm"
          width="100%"
          minWidth={0}
        >
          <XStack
            gap="$spacing-inset-quarck"
            alignItems="center"
            mb="$spacing-inset-nano"
          >
            <HeroIcon icon={['fal', 'lightbulb']} size={20} color="$accent" />
            <Text
              color="$accent"
              fontSize="$font-size-xs"
              fontFamily="$mulishBold"
              fontWeight={700}
            >
              Comentário da questão
            </Text>
          </XStack>
          <RenderHtml
            tagsStyles={{
              p: {
                margin: 0,
                padding: 0,
              },
            }}
            contentWidth={htmlContentWidth}
            source={{ html: statement }}
            baseStyle={{
              color: textColor,
              fontSize: 14,
              fontFamily: 'Mulish-Regular',
              lineHeight: 21,
            }}
          />
        </View>
      )}
    </YStack>
  )
}
